import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { Case } from "@/lib/api";
import { CardIcon } from "@/components/CardIcon";

// Theme-aware pill classes (globals.css) so difficulty colors adapt to dark mode.
const difficultyConfig: Record<string, { key: "beginner" | "intermediate" | "advanced"; pill: string }> = {
  beginner:     { key: "beginner",     pill: "pill-success" },
  intermediate: { key: "intermediate", pill: "pill-warn" },
  advanced:     { key: "advanced",     pill: "pill-danger" },
};

export function CaseCard({ c, sessionCount, onStart, view = "card", unreadReports = 0 }: { c: Case; sessionCount: number; onStart: () => void; view?: "card" | "list"; unreadReports?: number }) {
  const router = useRouter();
  const t = useTranslations("dashboard.caseCard");
  const locale = useLocale();
  const diff = difficultyConfig[c.difficulty_level] ?? difficultyConfig.beginner;
  const complaint = locale === "tr" && c.presenting_complaint_tr ? c.presenting_complaint_tr : c.presenting_complaint;

  // The supervisor report is the most expensive thing the product produces, and the
  // "ready" toast is easy to miss. A count rather than a bare dot: a backlog of three
  // unread reports is a different message than one, and the number says so.
  const unreadBadge = unreadReports > 0 ? (
    <span
      aria-hidden
      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-[18px] text-center text-white shadow-sm"
      style={{ background: "var(--accent-warm)" }}
    >
      {unreadReports > 9 ? "9+" : unreadReports}
    </span>
  ) : null;
  // Screen readers get the same fact as words, inside the button's label, since the
  // badge itself is decorative markup.
  const unreadSrText = unreadReports > 0 ? (
    <span className="sr-only"> ({t("unreadReports", { count: unreadReports })})</span>
  ) : null;

  const badge = (
    <span className={`pill ${diff.pill} flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0`}>
      {t(`difficulty.${diff.key}`)}
    </span>
  );

  if (view === "list") {
    return (
      <div
        className="rounded-2xl p-4 border flex items-center gap-4 shadow-sm transition-all duration-200 hover:shadow-md hover:border-[var(--accent-mid)]"
        style={{ background: "var(--surface)", borderColor: "var(--border)" }}
      >
        <CardIcon type="patient" size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h3 className="font-semibold text-base text-[var(--text-primary)] truncate" style={{ fontFamily: "var(--font-lora)" }}>
              {c.name}, {c.age}
            </h3>
            {badge}
          </div>
          <p className="text-sm text-[var(--text-secondary)] truncate mt-0.5">{complaint}</p>
        </div>
        <span className="hidden lg:block text-xs text-[var(--text-muted)] shrink-0 whitespace-nowrap">
          {t("sessionsCompleted", { count: sessionCount })}
        </span>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onStart}
            className="text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors whitespace-nowrap"
            style={{ background: 'var(--accent)' }}
            onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
            onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
          >
            {t("startSession")}
          </button>
          <button
            onClick={() => router.push(`/sessions/${c.id}?patient=${encodeURIComponent(c.name)}`)}
            className="px-3 py-2 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
            title={t("viewHistory")}
          >
            {t("sessions")}
          </button>
          <span className="relative inline-flex">
            <button
              onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(c.name)}`)}
              className="px-3 py-2 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t("feedback")}
              {unreadSrText}
            </button>
            {unreadBadge}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-6 flex flex-col gap-4 border shadow-sm group transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-[var(--accent-mid)]"
      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <CardIcon type="patient" />
          <h3 className="font-semibold text-lg text-[var(--text-primary)] truncate" style={{ fontFamily: "var(--font-lora)" }}>
            {c.name}, {c.age}
          </h3>
        </div>
        {badge}
      </div>

      <p className="text-sm text-[var(--text-secondary)] leading-relaxed flex-1">{complaint}</p>

      <div className="flex items-center text-xs text-[var(--text-muted)]">
        <span>{t("sessionsCompleted", { count: sessionCount })}</span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onStart}
          className="flex-1 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors"
          style={{ background: 'var(--accent)', boxShadow: '0 2px 12px rgba(15,76,76,0.18)' }}
          onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
          onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
        >
          {t("startSession")}
        </button>
        <button
          onClick={() => router.push(`/sessions/${c.id}?patient=${encodeURIComponent(c.name)}`)}
          className="px-3 py-2.5 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
          title={t("viewHistory")}
        >
          {t("sessions")}
        </button>
        <span className="relative inline-flex">
          <button
            onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(c.name)}`)}
            className="px-3 py-2.5 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
          >
            {t("feedback")}
            {unreadSrText}
          </button>
          {unreadBadge}
        </span>
      </div>
    </div>
  );
}
